/**
 * 発注側アプリ（order.kenzai-order.potential-x.com・D-26・D-40）＝**M1 の主役**。
 *
 * 画面は 05 §6.2 の画面IDと 1:1（D-43）：
 *   A-01 注文する（カタログ）／A-03 注文・進捗／A-04 卸切替・招待／A-06 チェックアウト・完了
 *   A-02 図面から＝**M5**（作らない）／A-05 お知らせ＝**M2**（作らない）
 *   → 空のタブを出さない（05 §6.3 と同じ思想＝「近日公開」を並べない）。
 *
 * ★**M1.6（D-83）＝統合カタログ・統合カート**：接続している卸を1つ選んで使う形をやめ、
 *   **接続卸をまたいで**選び・1つのカートに入れ・一括で確定する（`POST /partner/checkout`）。
 * ★**M1.8（D-96）＝卸の絞り込み state を撤去した**。「いま選んでいる卸」という値をアプリが
 *   持たなくなったので、**1卸ぶんの文脈が要る画面は自分で決める**：
 *   - A-09 接続完了 …… **いま入れた招待コードの卸**（`nav("/welcome", { state })` で受け取る）
 *   - A-07 プロフィール … 画面内のチップで選ぶ（既定＝接続の古い方）
 *   それ以外は接続一覧をそのまま受け取って自分で束ねる（統合カタログ・統合カート）。
 *
 * トークンの扱い（02 §4.2）：**localStorage に置かない**＝メモリ保持。XSS で持ち出されない。
 * リロードで消えるが LIFF の ID トークンからサイレント再取得できる（06 §9）。
 *
 * 見た目の正は Claude Design（D-45）＝ここは semantic トークンのみ参照し、生の色値を書かない。
 */

import { ApiError, auth, partnerApi, type PartnerToken } from "@kenzai/api-client";
import { AuthCard, Button, ErrorNote, Field, FormError } from "@kenzai/ui";
import { config } from "@kenzai/config";
import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";

import { CartProvider } from "./cart";
import { CartUiProvider } from "./cartUi";
import { CartSheets } from "./CartSheets";
import { LiffSignIn } from "./liffAuth";
import { Catalog } from "./pages/Catalog";
import { OrderDetail } from "./pages/OrderDetail";
import { Orders } from "./pages/Orders";
import { Privacy } from "./pages/Privacy";
import { Profile } from "./pages/Profile";
import { SavedSets } from "./pages/SavedSets";
import { SiteCosts } from "./pages/SiteCosts";
import { SwitchTenant } from "./pages/SwitchTenant";
import { Welcome } from "./pages/Welcome";
import { Shell } from "./Shell";

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
});

export function App() {
  const [token, setToken] = useState<PartnerToken | null>(null);

  // 短命JWT（1h）が切れたら**ログイン画面へ戻す**＝LIFF 環境ならそのまま黙って取り直される
  // （SignIn が自動で走るため利用者の操作は要らない）。dev は開発用フォームが出る。
  const onExpired = useCallback(() => setToken(null), []);

  // ★プライバシーポリシーは**ログインの外**（T-D29・09 §K.3）＝友だち追加の前・接続の前に
  //   読めなければ「同意のうえで使い始める」導線にならない。ログインを挟むと、まだ何も
  //   同意していない人にログインを求めることにもなる。
  if (typeof window !== "undefined" && window.location.pathname === "/privacy") return <Privacy />;

  if (!token) return <SignIn onSuccess={setToken} />;
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Authed token={token} onExpired={onExpired} />
      </BrowserRouter>
    </QueryClientProvider>
  );
}

/**
 * ログイン（FR-117）。**status=unlinked/blocked でも認証自体は成功する**（06 §9）＝
 * 招待コード入力へ誘導するのが M1（ここで弾くと、まだ接続していない職人が何もできない）。
 *
 * `config.liffId`（`VITE_LIFF_ID`）が入っていれば**実 LINE ログイン**、無ければ開発用フォーム。
 * stg/prod は `DEV_FAKE_LIFF` が起動ガードで禁止されている（config.assert_production_safety）ので、
 * 実質「LIFF ID を渡した環境＝実 LINE、渡していない環境＝dev」で 1:1 に対応する。
 */
function SignIn({ onSuccess }: { onSuccess: (t: PartnerToken) => void }) {
  const [id, setId] = useState("1");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (config.liffId) return <LiffSignIn liffId={config.liffId} onSuccess={onSuccess} />;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onSuccess(await auth.liff(id));
    } catch (err) {
      setError(
        err instanceof ApiError
          ? `${err.message}（サーバ側で DEV_FAKE_LIFF=1 が必要です）`
          : "ログインできませんでした。",
      );
      setBusy(false);
    }
  }

  return (
    <AuthCard title="建材オーダー" subtitle="開発用ログイン（LINE の代わり）">
      <form onSubmit={submit} noValidate data-testid="dev-login-form">
        <FormError message={error} />
        <Field
          label="職人ID（開発用）"
          hint="本番は LINE の ID トークンで自動ログインします（この画面は出ません）。"
          value={id}
          onChange={(e) => setId(e.target.value)}
          required
        />
        <Button fullWidth type="submit" busy={busy}>
          ログイン
        </Button>
      </form>
    </AuthCard>
  );
}

/**
 * 招待リンク（06 §2.2）で渡ってきたコードを読む＝`https://liff.line.me/{LIFF_ID}/invite?invite=XXXXXXXX`。
 *
 * ★ `?code=` ではなく **`?invite=`**：外部ブラウザで LIFF を開くと LINE Login の認可コードが
 *   **同じ `code` という名前で**同じ URL に乗り、SDK のクエリ解析が重複キーを配列にするため
 *   トークン交換が壊れる（＝招待リンクを配ると誰も入れない）。
 * ★ `liffAuth.tsx` の `cleanUrl()` が消すキー一覧に **`invite` を足してはいけない**＝
 *   足すと LINE ログインの往復のたびにプレフィルが消える。
 */
function inviteFromUrl(): string {
  if (typeof window === "undefined") return "";
  return new URLSearchParams(window.location.search).get("invite") ?? "";
}

function Authed({ token, onExpired }: { token: PartnerToken; onExpired: () => void }) {
  const client = useMemo(() => partnerApi(token.access_token), [token.access_token]);

  // ★キーにトークンを含める：含めないと、再ログイン直後のマウントで**前のトークンで失敗した
  //   キャッシュ（401）**をそのまま読み、再取得の結果を待たずに下の effect が発火して
  //   ログイン画面へ戻す＝LINE の検証を1往復むだに叩く（レビュー指摘）。
  const connections = useQuery({
    queryKey: ["connections", token.access_token],
    queryFn: () => client.connections(),
  });

  // JWT 失効（1h）＝**ログイン画面へ戻す**。ここで判定するのは接続一覧＝どの画面でも必ず走る
  // 唯一のクエリだから（各画面に同じ処理を撒かない）。render 中に setState しない＝effect で。
  const expired = connections.isError && connections.error instanceof ApiError && connections.error.status === 401;
  useEffect(() => {
    if (expired) onExpired();
  }, [expired, onExpired]);

  if (connections.isLoading || expired) return <Splash>読み込み中…</Splash>;
  if (connections.isError) {
    return (
      <Splash>
        <ErrorNote error={connections.error} />
      </Splash>
    );
  }

  const list = connections.data ?? [];
  // **接続0本＝招待コードの入力へ**（D-18＝招待制。勝手に卸は見えない）。
  // ★ここにも `?invite=` を渡す＝**招待リンクの主対象は初回の職人**（まだ接続0本）なので、
  //   /invite ルート側にだけ渡すとプレフィルが一番効いてほしい場面で効かない。
  if (list.length === 0) {
    return (
      <Splash>
        <SwitchTenant client={client} connections={list} standalone prefill={inviteFromUrl()} />
      </Splash>
    );
  }

  return (
    <CartProvider>
      <CartUiProvider>
      {/* ★画面を移ったら**先頭から読む**（D-97①）＝カタログの下の方から「カート」を押すと、
          スクロール位置がそのまま残って1社目の卸名が隠れた状態で着地していた（実測 scrollY=127）。
          戻る操作の位置復元はしない（M1.8 のスコープ外＝復元するなら履歴ごとに覚える話になる）。 */}
      <ScrollToTop />
      <Routes>
        <Route element={<Shell />}>
          <Route path="/" element={<Navigate to="/catalog" replace />} />
          {/* A-01 統合カタログ（D-83・FR-126／M1.8＝商品起点・D-95）＝接続卸を横断（fan-in はクライアント側） */}
          <Route path="/catalog" element={<Catalog client={client} connections={list} />} />
          {/* ★注文履歴は**常に全卸**（D-96⑥）＝絞り込みが無くなったので分岐も消えた */}
          <Route path="/orders" element={<Orders client={client} connections={list} />} />
          {/* 注文詳細（D-88）＝**LINE 通知のディープリンクの着地**。tenant_id を URL に持つのは、
              子注文が「どの卸のものか」を知らないと接続ごとの API を叩けないため。 */}
          <Route
            path="/orders/:tenantId/:orderId"
            element={<OrderDetail client={client} connections={list} />}
          />
          {/* A-11 保存済みセット（FR-132・D-100）＝「いつもの注文」をワンタップでカートへ */}
          <Route path="/sets" element={<SavedSets client={client} connections={list} />} />
          {/* A-09 接続完了ステップ（D-78・FR-125）＝招待コードを入れた直後の着地。
              ★**どの卸とつながったかは `location.state` で受け取る**（D-96⑤）＝以前は
                「絞り込みの値」がその役をしていたので、絞り込みを撤去すると**2社目の招待を
                入れた職人に1社目の卸名を見せ、氏名を別の卸に保存する**ところだった。 */}
          <Route path="/welcome" element={<Welcome client={client} connections={list} />} />
          {/* A-08 現場・原価管理（FR-124・T-D41）＝**接続している卸すべて**を合算する */}
          <Route path="/sites" element={<SiteCosts client={client} connections={list} />} />
          {/* A-07 プロフィールは**卸ごと**（D-63）＝対象の卸は画面内のチップで選ぶ（D-96⑦） */}
          <Route path="/profile" element={<Profile api={client} connections={list} />} />
          {/* A-04 卸一覧・接続＝M1.8 で**接続管理に専任**（絞り込み役を返上・D-96⑥） */}
          <Route path="/switch" element={<SwitchTenant client={client} connections={list} />} />
          {/* 招待リンクの着地点（06 §1.1・§2.2）。既に接続がある職人が2社目の招待を
              受け取ったときもここに来る＝卸一覧と同じ画面でコードだけ充填する。 */}
          <Route
            path="/invite"
            element={<SwitchTenant client={client} connections={list} prefill={inviteFromUrl()} />}
          />
        </Route>
        <Route path="/privacy" element={<Privacy />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      {/* ★M1.9b（D-101）＝カート・レジ・完了は**ルートでなくシート**（Design の流れそのもの）。
          Routes の外＝どの画面の上にも出る。招待画面（接続0本）では出さない＝上の早期 return が先。 */}
      <CartSheets client={client} connections={list} />
      </CartUiProvider>
    </CartProvider>
  );
}

/**
 * 画面が変わったら先頭へ戻す（D-97①）。
 *
 * ★**何も描かない**（`null` を返す）＝レイアウトに1pxも足さない。`Routes` の外側・同じ
 *   `BrowserRouter` の中に置くので、どのタブ・どの遷移でも1箇所で効く。
 * ★`pathname` だけを見る（`search` や `state` は見ない）＝招待コードの URL 掃除
 *   （`history.replaceState`）や、同じ画面のクエリ変更でスクロールを飛ばさない。
 */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function Splash({ children }: { children: React.ReactNode }) {
  return <main style={{ padding: "var(--kz-space-5)", maxWidth: 520, margin: "0 auto" }}>{children}</main>;
}

function NotFound() {
  const nav = useNavigate();
  return (
    <Splash>
      <p>ページが見つかりません。</p>
      <Button fullWidth onClick={() => nav("/catalog")}>注文する画面へ</Button>
    </Splash>
  );
}
